import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { IoMdClose } from "react-icons/io";
import { BsQrCode } from "react-icons/bs";

import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import axios from "axios";
import { LiaEdit } from "react-icons/lia";
import { QrCode, StandaloneTable } from "../../types/globalTypes";
import { qrCodeUrlRegex } from "@/utils/Regexes";
import { ArabicOrdinal, EnglishOrdinal } from "@/types/enums";
import useLockBodyScroll from "@/hooks/useLockBodyScroll";

type QrCodeEditModalProps = Pick<
  QrCode,
  "_id" | "link" | "type" | "floor" | "table"
> & {
  setNeedRefetch: React.Dispatch<React.SetStateAction<boolean>>;
};

const QrCodeEditModal: React.FC<QrCodeEditModalProps> = ({
  _id,
  link,
  type,
  setNeedRefetch,
  table,
  floor,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  useLockBodyScroll(isModalOpen);
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const [allTables, setAllTables] = useState<StandaloneTable[]>([]);

  useEffect(() => {
    if (!token) return;
    const url = `${BASE_URL}/api/dashboard/floors/tables?locale=${lang}`;

    async function fetchTables() {
      try {
        const res = await axios.get(url, {
          headers: {
            Authorization: token,
          },
        });

        if (!res.data.success) {
          const messsage =
            res.data.error || t("qrCodeEditModal.failedToFetchTables")
          toast.error(messsage);
          return;
        }
        setAllTables(res.data.tables);
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const message =
            error.response?.data?.error ||
            t("qrCodeEditModal.failedToFetchTables")
          toast.error(message);
        } else {
          console.error(error);
        }
      }
    }

    fetchTables();
  }, [BASE_URL, lang, token, t]);

  const QrcodeTypeOptions = [
    { value: "link", label: t("qrCodeEditModal.link") },
    { value: "table", label: t("qrCodeEditModal.table") }
  ];

  const TablesOptions = useMemo(() => {
    if (!allTables.length) return [];
    return allTables.map((table: StandaloneTable) => ({
      value: JSON.stringify({ tableId: table._id, floorId: table.floorId }),
      label: t("qrCodeEditModal.tableLabel", { branch: i18n.language === "ar" ? table?.branch.nameAR : table?.branch.nameEN, floor: i18n.language === "ar" ? ArabicOrdinal[Number(table?.floorNumber) - 1] : EnglishOrdinal[Number(table?.floorNumber) - 1], tableNumber: table?.number })

    }));
  }, [allTables, i18n.language, t]);

  const validationSchema = Yup.object().shape({
    type: Yup.string().required(
      t("qrCodeEditModal.typeRequired") as string,
    ),
    link: Yup.string().when("type", {
      is: "link",
      then: (schema) =>
        schema
          .required(t("qrCodeEditModal.linkRequired") as string)
          .matches(
            qrCodeUrlRegex,
            t("qrCodeEditModal.invalidUrl") as string,
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    tableId: Yup.string().when("type", {
      is: "table",
      then: (schema) =>
        schema.required(t("qrCodeEditModal.tableRequired") as string),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const formik = useFormik({
    initialValues: {
      type: type || "link",
      link: link || "",
      tableId:
        table?._id && floor?._id
          ? JSON.stringify({ tableId: table?._id, floorId: floor?._id })
          : "",
    },
    validationSchema,
    onSubmit: (values) => {
      const { link, type, tableId } = values;

      const requestBody = {
        type,
        link: type === "link" ? link : undefined,
        tableId: type === "table" ? JSON.parse(tableId)?.tableId : undefined,
        floorId: type === "table" ? JSON.parse(tableId)?.floorId : undefined,
      };

      async function editQrCode() {

        const LoadingToast = toast.loading(
          t("qrCodeEditModal.editingQrCode") as string,
        );
        try {
          const url = `${BASE_URL}/api/dashboard/qr-codes/${_id}/link?locale=${lang}`;
          const response = await axios.put(url, requestBody, {
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
          });

          if (response.data.success === false) {
            toast.error(
              response.data.error ||
              (t("qrCodeEditModal.failedToEditQrCode") as string),
            );
            return;
          }

          toast.success(
            t("qrCodeEditModal.qrCodeEditedSuccessfully") as string,
          );
          setIsModalOpen(false);
          formik.resetForm();
          setNeedRefetch(true);
        } catch (error) {
          if (axios.isAxiosError(error)) {
            const errorMessage =
              error.response?.data?.error ||
              (t("qrCodeEditModal.failedToEditQrCode") as string);
            toast.error(errorMessage);
          } else {
            toast.error(
              t("qrCodeEditModal.unexpectedError") as string,
            );
          }
        } finally {
          toast.dismiss(LoadingToast);
        }
      }

      editQrCode();
    },
  });
  const handleOutsideClick = useCallback((event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setIsModalOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [handleOutsideClick, isModalOpen]);

  return (
    <>
      <button
        className="flex h-[32.5px] w-full items-center justify-center"
        onClick={(e) => {
          setIsModalOpen(true);
          e.stopPropagation();
        }}
      >
        <LiaEdit size={23} className="w-full" />
      </button>

      <div onClick={(e) => e.stopPropagation()}>
        {isModalOpen && (
          <div
            className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/50 pt-[70px] text-black"
            aria-labelledby="modal-title"
            role="dialog"
            aria-modal="true"
          >
            <div
              ref={modalRef}
              className="flex w-[95%] max-w-3xl flex-col gap-7 rounded-lg bg-white px-2 py-3 shadow-lg sm:px-4 sm:py-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-sm font-semibold sm:text-base md:text-2xl">
                  <BsQrCode className="text-xl" />
                  <h2 className="text-lg font-semibold">
                    {t("qrCodeEditModal.editQrCode")}                  </h2>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="mb-3 text-gray-600 hover:text-gray-800"
                >
                  <IoMdClose size={24} className="text-red-600" />
                </button>
              </div>

              <form
                id="edit-qrcode-form"
                onSubmit={formik.handleSubmit}
                className="thin-scrollbar flex max-h-[55dvh] flex-col gap-6 overflow-y-auto px-1"
              >

                <div className="flex w-full flex-row items-center gap-5 px-2">
                  {/* Link */}
                  {formik.values.type === "link" && (
                    <div className="mb-2 flex flex-grow flex-col self-start">
                      <label className="text-start text-sm font-medium text-gray-700">
                        {t("qrCodeEditModal.link")}
                      </label>

                      <div dir="ltr" className="relative">
                        <input
                          type="text"
                          name="link"
                          placeholder={"www.YourDomain.com or YourDomain.com"}
                          value={formik.values.link}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="mt-1 w-full rounded-md border border-gray-300 p-2 ps-[77px] text-base focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        <span className="absolute inset-y-0 start-0 mt-1 flex h-[calc(100%-3.7px)] w-[70px] items-center justify-center rounded-s-md bg-gray-300 text-center">
                          https://
                        </span>
                      </div>
                      {formik.touched.link && formik.errors.link && (
                        <div className="mt-1 text-start text-xs text-red-500">
                          {formik.errors.link}
                        </div>
                      )}
                    </div>
                  )}

                  {formik.values.type === "table" && (
                    <div className="mb-2 flex flex-grow flex-col self-start">
                      <label className="text-start text-sm font-medium text-gray-700">
                        {t("qrCodeEditModal.chooseTable")}
                      </label>

                      <div className="relative">
                        <select
                          value={formik.values.tableId}
                          onChange={formik.handleChange}
                          name="tableId"
                          id="tableId"
                          className="mt-1 w-full rounded-md border border-gray-300 p-2 text-base focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="" disabled>
                            --{" "}{t("qrCodeEditModal.selectTable")}{" "}--
                          </option>
                          {TablesOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {formik.touched.tableId && formik.errors.tableId && (
                        <div className="mt-1 text-start text-xs text-red-500">
                          {formik.errors.tableId}
                        </div>
                      )}
                    </div>
                  )}

                  {/* type */}
                  <div className="mb-2 flex flex-col self-start">
                    <label className="text-start text-sm font-medium text-gray-700">
                      {t("qrCodeEditModal.type")}
                    </label>

                    <select
                      value={formik.values.type}
                      onChange={formik.handleChange}
                      name="type"
                      id="type"
                      className="mt-1 w-full rounded-md border border-gray-300 p-2 text-base focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {QrcodeTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </form>
              {/* Submit */}
              <button
                type="submit"
                form="edit-qrcode-form"
                className="w-fit self-end rounded-md bg-sidebar px-4 py-2 text-skyblue"
              >
                {t("qrCodeEditModal.editQrCode")}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default QrCodeEditModal;
